<template>
  <div class="warranty-report-by-employee" @keyup.enter="onFilter">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    />
    <KTCodePreview title="Báo cáo theo nhân viên">
      <template v-slot:toolbar>
        <div class="row">
          <div>
            <b-dropdown
              size="sm"
              id="dropdown-actions"
              right
            >
              <template slot="button-content">
                <i
                  style="font-size: 1rem"
                  class="fas fa-cog"
                ></i>
                <span class="font-weight-bolder">Thao tác</span>
              </template>
              <b-dropdown-item v-if="checkPermission('WARRANTY_REPORT_BY_EMPLOYEE_EXPORT')" @click="onExportExcel">
                <span>
                  <i
                    style="font-size: 1rem"
                    class="far fa-file-excel"
                  ></i>
                  &nbsp; Xuất Excel
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </template>
      <template v-slot:preview>
        <!-- Bộ lọc -->
        <b-row class="mb-3 align-items-end">
          <b-col
            cols="12"
            md="2"
          >
            <date-picker
              v-model="filter.fromDate"
              class="form-control form-control-sm"
              :config="DP_CONFIG.date"
              placeholder="Từ ngày"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <date-picker
              v-model="filter.toDate"
              class="form-control form-control-sm"
              :config="DP_CONFIG.date"
              placeholder="Đến ngày"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <EmployeeSearch
              :value="filter.technicianName"
              :employee-id="filter.technicianId"
              placeholder="Tên hoặc mã nhân viên"
              @employee-selected="onTechnicianSelected"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <BDropdownCustom
              :value="statusSelectedObjects"
              :options="statusOptions"
              placeholder="Chọn trạng thái"
              :searchable="true"
              :match-key="['name', 'id']"
              text-prop="name"
              value-prop="id"
              @valueChange="onStatusChange"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <BDropdownCustom
              :value="criteriaSelectedObjects"
              :options="criteriaOptions"
              placeholder="Đặc điểm"
              :searchable="true"
              :match-key="['name']"
              text-prop="name"
              value-prop="id"
              @valueChange="onCriteriaChange"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <b-form-select
              v-model="filter.companyId"
              size="sm"
              class="form-control"
              :options="listCompany"
              value-field="id"
              text-field="name"
            />
          </b-col>
          <b-col
            cols="12"
            md="auto"
            class="d-flex align-items-end mt-3"
          >
            <b-button
              variant="primary"
              size="sm"
              class="mr-2"
              :disabled="isLoading"
              @click="onFilter"
            >
              <i class="fas fa-search mr-1"></i>Lọc
            </b-button>
            <b-button
              class="btn font-weight-bold"
              variant="secondary"
              size="sm"
              @click="onClearFilter"
              :disabled="isLoading || activeFilterCount === 0"
            >
              Xóa bộ lọc
              <span v-if="activeFilterCount > 0">({{ activeFilterCount }})</span>
            </b-button>
          </b-col>
        </b-row>

        <b-card
          no-body
          class="mb-0 border-0 shadow-none"
        >
          <b-card-header class="d-flex justify-content-between align-items-center py-2">
          </b-card-header>
          <b-card-body class="p-0">
            <b-table
              :items="reportPaginated"
              :fields="reportTableFields"
              bordered
              hover
              small
              :busy="isLoading"
              class="table-bordered table-hover table-sm"
              show-empty
              empty-text="Chưa có dữ liệu"
            >
              <template #table-busy>
                <vcl-table
                  :speed="5"
                  :animate="true"
                  :columns="6"
                />
              </template>
              <template #cell(stt)="row">
                <span>{{ row.item.stt }}</span>
              </template>
              <template #cell(employeeDisplay)="row">
                <span>{{ row.item.fullName || '—' }}</span>
                <br />
                <span
                  v-if="row.item.employeeCode"
                  class="text-muted small ml-1"
                  >({{ row.item.employeeCode }})</span
                >
              </template>
              <template #cell(totalRepairing)="row">
                <span class="text-center">{{ convertPrice(row.item.totalRepairing) }}</span>
              </template>
              <template #cell(totalReturned)="row">
                <span class="text-center">{{ convertPrice(row.item.totalReturned) }}</span>
              </template>
              <template #cell(totalWarranty)="row">
                <span class="text-center">{{ convertPrice(row.item.totalWarranty) }}</span>
              </template>
              <template #cell(totalPartsExported)="row">
                <span class="text-center">{{ convertPrice(row.item.totalPartsExported) }}</span>
              </template>
              <template #cell(totalAmountExported)="row">
                <span class="text-center">{{
                  convertPrice(row.item.totalAmountExported)
                }}</span>
              </template>
              <template #cell(totalGrossProfitExported)="row">
                <span class="text-center">{{
                  convertPrice(row.item.totalGrossProfitExported)
                }}</span>
              </template>
              <template #cell(stockSlipCodes)="row">
                <div class="text-center">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="!((row.item.replaceProducts || []).length)"
                    @click="openStockSlipModal(row.item)"
                  >
                    <i class="fas fa-list-ul"></i>
                  </b-button>
                </div>
              </template>
            </b-table>
          </b-card-body>
          <b-card-footer class="d-flex justify-content-between align-items-center py-2">
            <span class="font-weight-bold">
              Hiển thị {{ reportFrom }}–{{ reportTo }} / {{ totalItems }} bản ghi
            </span>
            <BPaginationNavCustom
              v-if="numberOfPage > 1"
              :number-of-pages="numberOfPage"
              :current-page="currentPage"
              :total-page="numberOfPage"
              @page-change="onPageChange"
            />
          </b-card-footer>
        </b-card>

        <ReplaceProductWarrantyReportModal
          :visible="showStockSlipModal"
          :title="stockSlipModalTitle"
          :rows="stockSlipRows"
          :has-export-excel="checkPermission('WARRANTY_REPORT_BY_EMPLOYEE_EXPORT')"
          @close="hideStockSlipModal"
        />
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import Loader from '@/view/content/Loader.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import timeUtils from '@/utils/date'
import { WARRANTY_STATUS_OPTIONS } from '@/utils/constants'
import apiCommonMixin from '@/view/mixins/api-common.mixins'
import { convertPrice } from '@/utils/common'
import {
  getWarrantyReportsByEmployee,
  exportWarrantyReportByEmployeeExcel,
} from '../utils/warrantyApi'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import EmployeeSearch from '@/view/components/common/EmployeeSearch.vue'
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue'
import { VclTable } from 'vue-content-loading'
import ReplaceProductWarrantyReportModal from './components/ReplaceProductWarrantyReportModal.vue'
import permissionMixin from '@/view/mixins/permission.mixin'

export default {
  mixins: [apiCommonMixin, permissionMixin],
  components: {
    KTCodePreview,
    Loader,
    BPaginationNavCustom,
    EmployeeSearch,
    VclTable,
    BDropdownCustom,
    ReplaceProductWarrantyReportModal,
  },
  data() {
    return {
      DP_CONFIG: timeUtils.DP_CONFIG,
      filter: {
        fromDate: timeUtils.getStartOfMonth(),
        toDate: null,
        technicianId: null,
        technicianName: '',
        status: [],
        criteriaIds: [],
        companyId: null,
      },
      listCompany: [],
      criteriaOptions: [
        { id: 'HAS_DEBT', name: 'Còn công nợ' },
        { id: 'NO_DEBT', name: 'Hết công nợ' },
        { id: 'CANNOT_REPAIR', name: 'Không sửa được' },
        { id: 'DISAGREE_PRICE', name: 'Không đồng ý giá' },
      ],
      listItem: [],
      totalItems: 0,
      numberOfPage: 1,
      limit: 10,
      isLoading: false,
      showStockSlipModal: false,
      stockSlipRows: [],
      stockSlipModalTitle: 'danh-sach-phieu-xuat-linh-kien',
      reportTableFields: [
        { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '5%' } },
        { key: 'employeeDisplay', label: 'Kĩ thuật viên', thStyle: { width: '20%' } },
        {
          key: 'totalRepairing',
          label: 'SL đang sửa',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'totalReturned',
          label: 'SL đã trả',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'totalWarranty',
          label: 'SL bảo hành',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'totalPartsExported',
          label: 'SL LK',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'totalAmountExported',
          label: 'Tổng tiền xuất LK/DV',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '15%' },
        },
        {
          key: 'totalGrossProfitExported',
          label: 'Lãi gộp',
          tdClass: 'text-right',
          thClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'stockSlipCodes',
          label: 'Chi tiết',
          tdClass: 'text-left',
          thClass: 'text-center',
          tdStyle: { width: '10%' },
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    activeFilterCount() {
      const f = this.filter
      let count = 0
      if (f.fromDate) count++
      if (f.toDate) count++
      if (f.technicianId) count++
      if (f.status && (!Array.isArray(f.status) || f.status.length > 0)) count++
      if ((Array.isArray(f.criteriaIds) ? f.criteriaIds : []).length > 0) count++
      if (f.companyId) count++
      return count
    },
    criteriaSelectedObjects() {
      const ids = this.filter.criteriaIds || []
      return this.criteriaOptions.filter((c) => ids.includes(c.id))
    },
    statusSelectedObjects() {
      const selected = Array.isArray(this.filter.status) ? this.filter.status : []
      return this.statusOptions.filter((s) => selected.includes(s.id))
    },
    currentPage() {
      return Math.max(1, Number(this.$route.query.page) || 1)
    },
    statusOptions() {
      return WARRANTY_STATUS_OPTIONS
    },
    reportFrom() {
      if (this.totalItems === 0) return 0
      return (this.currentPage - 1) * this.limit + 1
    },
    reportTo() {
      const end = (this.currentPage - 1) * this.limit + (this.listItem || []).length
      return Math.min(end, this.totalItems)
    },
    reportPaginated() {
      const list = this.listItem || []
      const baseStt = (this.currentPage - 1) * this.limit
      return list.map((item, idx) => ({ ...item, stt: baseStt + idx + 1 }))
    },
  },
  created() {
    this.fetchCompany()
    this.fetchData()
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Báo cáo theo nhân viên' }])
  },
  methods: {
    convertPrice,
    openStockSlipModal(rowItem) {
      const rows = (Array.isArray(rowItem?.replaceProducts) ? rowItem.replaceProducts : []).map(
        (item, idx) => ({
          ...item,
          index: idx,
        })
      )
      this.stockSlipRows = rows
      this.stockSlipModalTitle = `Danh sách phiếu xuất linh kiện - ${rowItem?.fullName || ''}`
      this.showStockSlipModal = true
    },
    hideStockSlipModal() {
      this.showStockSlipModal = false
    },
    async fetchCompany() {
      const list = await this.fetchCompanies()
      this.listCompany = [{ id: null, name: 'Tất cả doanh nghiệp' }, ...list]
    },
    onTechnicianSelected(employee) {
      this.filter.technicianId = employee ? employee.id : null
      this.filter.technicianName = employee ? employee.name : ''
    },
    onClearFilter() {
      this.filter = {
        fromDate: null,
        toDate: null,
        technicianId: null,
        technicianName: '',
        status: [],
        criteriaIds: [],
        companyId: null,
      }
      this.$router.push({ name: 'warranty-report-by-employee', query: { page: 1 } }).catch(() => {})
      this.fetchData(1)
    },
    getParamFilters() {
      return {
        page: this.currentPage,
        limit: this.limit,
        primaryTechnicianId: this.filter.technicianId || undefined,
        fromDate: this.filter.fromDate && timeUtils.formatDBDate(this.filter.fromDate),
        toDate: this.filter.toDate && timeUtils.formatDBDate(this.filter.toDate),
        companyId: this.filter.companyId || undefined,
        statuses: this.filter.status?.length ? this.filter.status : undefined,
        criteriaIds:
          Array.isArray(this.filter.criteriaIds) && this.filter.criteriaIds.length
            ? this.filter.criteriaIds
            : undefined,
      }
    },
    onStatusChange(selectedItems = []) {
      this.filter.status = selectedItems.map((s) => s.id)
    },
    onCriteriaChange(selectedObjects) {
      this.filter.criteriaIds = (selectedObjects || []).map((o) => o.id)
    },
    onFilter() {
      this.$router.push({ name: 'warranty-report-by-employee', query: { page: 1 } }).catch(() => {})
      this.fetchData(1)
    },
    async onExportExcel() {
      const params = this.getParamFilters()
      delete params.page
      delete params.limit
      await exportWarrantyReportByEmployeeExcel(params)
    },
    onPageChange(page) {
      this.$router
        .push({ name: 'warranty-report-by-employee', query: { ...this.$route.query, page } })
        .catch(() => {})
      this.fetchData(Number(page))
    },
    async fetchData(overridePage) {
      if (this.isLoading) return
      this.isLoading = true
      const page = overridePage !== undefined ? overridePage : this.currentPage
      try {
        const params = { ...this.getParamFilters(), page }
        const data = await getWarrantyReportsByEmployee(params)
        this.totalItems = data.totalRow || 0
        this.numberOfPage = Math.max(1, data.totalPage || 1)
        this.listItem = data.dataset || []
      } catch (err) {
        this.listItem = []
        this.totalItems = 0
        this.numberOfPage = 1
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.warranty-report-by-employee {
  label.small {
    font-size: 0.8rem;
  }
}
</style>
